// @flow
import * as React from 'react';
import type { ReactSetStateFunction } from 'react';

import { View, RadioGroup } from 'reshaped';

import type { ChangeHandler } from 'reshaped';

import { Alert } from '@gandi/reshaped';
import { useTranslator } from '@gandi/translate';

import { RadioBlock } from './RadioBlock';
import type { Item } from './RadioBlock';

import styles from './RadioBlockGroup.module.css';

type Props<T: Item> = {
  children?: React.ComponentType<T>,
  items: Array<T>,
  name: string,
  /** disabled to true will unplug the radio selection */
  disabled?: boolean,
  renderLabel?: React.ComponentType<T>,
  renderNoOpt?: string,
  listFooterNotice?: string,
  initialValue?: string,
  value?: string,
  setValue: ReactSetStateFunction<string | void>,
};

function moveItemToFront<T: Item>(array: Array<T>, id: string): Array<T> {
  const index = array.findIndex(item => item.id === id);

  if (index <= 0) {
    return array;
  }

  const itemToMove = array[index];
  return [itemToMove, ...array.filter(item => item.id !== id)];
}

function GreyNotice({
  children,
}: {
  children: React.Node,
}): React.MixedElement {
  return (
    <div className={styles.root}>
      <Alert color="neutral">{children}</Alert>
    </div>
  );
}

export function RadioBlockGroup<T: Item>({
  children: Component,
  items,
  name,
  renderLabel: Label,
  value,
  initialValue,
  renderNoOpt,
  listFooterNotice,
  disabled,
  setValue,
}: Props<T>): React.MixedElement {
  const { __ } = useTranslator();

  const sortedItems = React.useMemo(
    () => (initialValue ? moveItemToFront(items, initialValue) : items),
    [items, initialValue],
  );

  const handleChange: ChangeHandler<string> | void = disabled
    ? undefined // unplug the action when the RadioBlockGroup is disabled
    : args => {
        if (args.value) setValue(args.value);
      };

  if (items.length === 0) {
    return <GreyNotice>{renderNoOpt || __('No options available')}</GreyNotice>;
  }

  return (
    <View paddingBottom={value ? undefined : 7} justify="space-between">
      <RadioGroup name={name} value={value || null} onChange={handleChange}>
        <View gap={3}>
          {sortedItems.map(item => {
            // Remove Key injected in old customer state
            // TODO remove this when we clean old resource
            const { key, ...extraItem } = item;
            return (
              <RadioBlock
                {...extraItem}
                selected={value === extraItem.id}
                value={extraItem.id}
                renderLabel={Label ? () => <Label {...extraItem} /> : undefined}
                key={extraItem.id}
              >
                {Component && <Component {...extraItem} />}
              </RadioBlock>
            );
          })}
        </View>
      </RadioGroup>

      {listFooterNotice && (
        <View.Item gapBefore={5}>
          <GreyNotice>{listFooterNotice}</GreyNotice>
        </View.Item>
      )}
    </View>
  );
}
